import * as Sentry from '@sentry/react';
import React from 'react';
import { Result, Button, Collapse, Flex } from '@nula/core-ui';
import styles from './app-error-boundary.module.css';

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      return (
        <Flex vertical align="center" justify="center" className={styles.fullViewport}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle="The app hit an unexpected error. Try recovering, or reload to start fresh."
            extra={
              <Flex vertical align="center" gap="sm">
                <Flex gap="sm">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => this.setState({ hasError: false, error: null })}
                  >
                    Try again
                  </Button>
                  <Button size="sm" variant="default" onClick={() => window.location.reload()}>
                    Reload
                  </Button>
                </Flex>
                {import.meta.env.DEV && error && (
                  <Collapse
                    ghost
                    compact
                    className={styles.devDetails}
                    items={[
                      {
                        key: 'error',
                        header: 'Error details',
                        children: (
                          <pre className={styles.errorStack}>
                            {error.message}
                            {error.stack ? `\n${error.stack}` : ''}
                          </pre>
                        ),
                      },
                    ]}
                  />
                )}
              </Flex>
            }
          />
        </Flex>
      );
    }

    return this.props.children;
  }
}
